/**
 * Encoder for webmail view links (`/mail?s=<token>`) so "Open email" links
 * don't show readable message ids / folders. Must stay byte-for-byte in sync
 * with EnterpriseMail/web/src/lib/mailUrlState.ts (the webmail decodes it).
 * Obfuscation only — access is enforced by the Mail BFF.
 */
export interface MailLinkState {
    folder?: string;
    m?: string;
}

const VERSION = 1;
const KEY = "fg.mail.url.v1";

function fnv1a(text: string): number {
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}

const KEY_SEED = fnv1a(KEY);

function keystream(length: number, salt: number): Uint8Array {
    let x = (KEY_SEED ^ Math.imul(salt + 1, 0x9e3779b1)) >>> 0 || 0x2545f491;
    const out = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        x ^= x << 13;
        x >>>= 0;
        x ^= x >>> 17;
        x ^= x << 5;
        x >>>= 0;
        out[i] = x & 0xff;
    }
    return out;
}

function checksum(bytes: Uint8Array): number {
    let sum = 0;
    bytes.forEach((b, i) => {
        sum = (sum + b * (i + 1)) & 0xff;
    });
    return sum;
}

/** Same compact JSON shape and field order as webmail's compactMailState. */
export function encodeMailLinkState(state: MailLinkState): string {
    const compact: MailLinkState = {};
    if (state.folder && state.folder !== "inbox") compact.folder = state.folder;
    if (state.m) compact.m = state.m;
    const plain = new TextEncoder().encode(JSON.stringify(compact));
    const salt = fnv1a(Array.from(plain, (b) => String.fromCharCode(b)).join("")) & 0xff;
    const stream = keystream(plain.length, salt);
    const out = new Uint8Array(plain.length + 3);
    out[0] = VERSION;
    out[1] = salt;
    out[2] = checksum(plain);
    for (let i = 0; i < plain.length; i++) out[i + 3] = (plain[i] ?? 0) ^ (stream[i] ?? 0);
    let binary = "";
    out.forEach((b) => {
        binary += String.fromCharCode(b);
    });
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
